// Developed by Softvative.com
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useIncidents } from '../../context/IncidentContext';

const {
    FiPlus, FiSearch, FiFilter, FiChevronDown, FiAlertTriangle, FiPlayCircle,
    FiCheckCircle, FiClock, FiArrowUp, FiArrowDown
} = FiIcons;

const IncidentList = () => {
    const { incidents, customFields, getCustomFieldValue } = useIncidents();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedPriority, setSelectedPriority] = useState('All');
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    const categories = ['All', ...new Set(incidents.map(i => i.category))];
    const statuses = ['All', ...new Set(incidents.map(i => i.status))];
    const priorities = ['All', 'Critical', 'High', 'Medium', 'Low'];

    const filteredIncidents = incidents.filter(incident => {
        const matchesSearch = incident.incidentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            incident.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            incident.service.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || incident.category === selectedCategory;
        const matchesStatus = selectedStatus === 'All' || incident.status === selectedStatus;
        const matchesPriority = selectedPriority === 'All' || incident.priority === selectedPriority;

        return matchesSearch && matchesCategory && matchesStatus && matchesPriority;
    });

    const sortedIncidents = [...filteredIncidents].sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        }

        if (sortOrder === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'New': return FiAlertTriangle;
            case 'In Progress': return FiPlayCircle;
            case 'Resolved': return FiCheckCircle;
            default: return FiClock;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'New': return 'blue';
            case 'In Progress': return 'yellow';
            case 'Resolved': return 'green';
            default: return 'gray';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'red';
            case 'High': return 'orange';
            case 'Medium': return 'yellow';
            case 'Low': return 'green';
            default: return 'gray';
        }
    };

    const getSLAStatus = (incident) => {
        if (incident.status === 'Resolved') return { status: 'On Track', color: 'green' };

        const slaHours = incident.priority === 'Critical' ? 1 :
            incident.priority === 'High' ? 4 :
                incident.priority === 'Medium' ? 8 : 24;

        const timeElapsed = (new Date() - new Date(incident.createdAt)) / (1000 * 60 * 60);

        if (timeElapsed > slaHours * 1.2) return { status: 'Breached', color: 'red' };
        if (timeElapsed > slaHours * 0.8) return { status: 'Warning', color: 'yellow' };
        return { status: 'On Track', color: 'green' };
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6 h-full overflow-auto"
        >
            {/* Header */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Incidents</h1>
                        <p className="text-gray-600 dark:text-gray-400">Manage and track service incidents</p>
                    </div>
                    <Link
                        to="/incidents/new"
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                        <span>New Incident</span>
                    </Link>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <SafeIcon
                            icon={FiSearch}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                        />
                        <input
                            type="text"
                            placeholder="Search incidents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    {/* Filter Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center space-x-2 px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <SafeIcon icon={FiFilter} className="w-4 h-4" />
                            <span>Filters</span>
                            <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                        </button>

                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 p-4"
                            >
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                                        <select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                        <select
                                            value={selectedStatus}
                                            onChange={(e) => setSelectedStatus(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        >
                                            {statuses.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                                        <select
                                            value={selectedPriority}
                                            onChange={(e) => setSelectedPriority(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        >
                                            {priorities.map(priority => (
                                                <option key={priority} value={priority}>{priority}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            {/* Results Count */}
            <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {sortedIncidents.length} of {incidents.length} incidents
                </p>
            </div>

            {/* Incidents Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                                    <button
                                        onClick={() => handleSort('id')}
                                        className="flex items-center space-x-1 hover:text-gray-900 dark:hover:text-white"
                                    >
                                        <span>ID</span>
                                        {sortBy === 'id' && (
                                            <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-3 h-3" />
                                        )}
                                    </button>
                                </th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                                    <button
                                        onClick={() => handleSort('description')}
                                        className="flex items-center space-x-1 hover:text-gray-900 dark:hover:text-white"
                                    >
                                        <span>Description</span>
                                        {sortBy === 'description' && (
                                            <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-3 h-3" />
                                        )}
                                    </button>
                                </th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Priority</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Category</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Service</th>
                                {/* Custom Fields Headers */}
                                {customFields.map(field => (
                                    <th key={field.id} className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                                        {field.name}
                                    </th>
                                ))}
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">SLA</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                                    <button
                                        onClick={() => handleSort('createdAt')}
                                        className="flex items-center space-x-1 hover:text-gray-900 dark:hover:text-white"
                                    >
                                        <span>Created</span>
                                        {sortBy === 'createdAt' && (
                                            <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-3 h-3" />
                                        )}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedIncidents.map((incident, index) => {
                                const slaStatus = getSLAStatus(incident);

                                return (
                                    <motion.tr
                                        key={incident.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    >
                                        <td className="py-3 px-4">
                                            <Link
                                                to={`/incidents/${incident.id}`}
                                                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                                            >
                                                {incident.id}
                                            </Link>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="max-w-xs">
                                                <p className="text-gray-900 dark:text-white truncate">{incident.description }</p>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full bg-${getPriorityColor(incident.priority)}-100 text-${getPriorityColor(incident.priority)}-800 dark:bg-${getPriorityColor(incident.priority)}-900 dark:text-${getPriorityColor(incident.priority)}-200`}>
                                                {incident.priority}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center space-x-2">
                                                <SafeIcon icon={getStatusIcon(incident.status)} className={`w-4 h-4 text-${getStatusColor(incident.status)}-600`} />
                                                <span className="text-gray-900 dark:text-white">{incident.status}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{incident.category}</td>
                                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{incident.service}</td>
                                        {/* Custom Fields Values */}
                                        {customFields.map(field => {
                                            const value = getCustomFieldValue(incident, field.id);
                                            return (
                                                <td key={field.id} className="py-3 px-4 text-gray-600 dark:text-gray-400">
                                                    {field.type === 'checkbox' ? (value ? 'Yes' : 'No') : (value || '-')}
                                                </td>
                                            );
                                        })}
                                        <td className="py-3 px-4">
                                            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full bg-${slaStatus.color}-100 text-${slaStatus.color}-800 dark:bg-${slaStatus.color}-900 dark:text-${slaStatus.color}-200`}>
                                                {slaStatus.status}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                                            <div className="text-sm">
                                                <p>{format(new Date(incident.createdAt), 'MMM dd, yyyy')}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-500">
                                                    {formatDistanceToNow(new Date(incident.createdAt), { addSuffix: true })}
                                                </p>
                                            </div>
                                        </td>
                                    </motion.tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {sortedIncidents.length === 0 && (
                    <div className="text-center py-12">
                        <SafeIcon icon={FiSearch} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">No incidents found</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                            Try adjusting your search or filters
                        </p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default IncidentList;